/**
 * Pure helpers for grouping `booking_state_changed` / `booking_rolled` child
 * effects under a `batch_ingested` root. The backend emits one child per
 * booking; the drill-down collapses those into `from → to` buckets so operators
 * scan transitions first, then open a nested list of the bookings in each.
 */
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import type { BookingStateChangedPayload, FeedEvent } from "@/types/feed";

const STATE_CHANGE_TYPES = new Set(["booking_state_changed", "booking_rolled"]);

/** Lifecycle order used to sort transition groups (later states first). */
const STATE_ORDER: readonly string[] = [
  "rebooked",
  "cancelled",
  "delivered",
  "shipped",
  "stuffed",
  "rolled",
  "book_restricted",
  "book",
  "pending_book",
  "pending",
];

export interface StateChangeTransition {
  fromState: string;
  toState: string;
}

export interface StateChangeGroup extends StateChangeTransition {
  key: string;
  events: FeedEvent[];
}

function readPayloadString(payload: unknown, field: string): string | null {
  if (!payload || typeof payload !== "object") return null;
  const value = (payload as Record<string, unknown>)[field];
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

/** Canonical snake_case key so "Pending Book" and `pending_book` group together. */
export function normalizeBookingStateKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/** Human label for a booking state (`pending_book` → "Pending book"). */
export function formatBookingStateLabel(raw: string): string {
  const key = normalizeBookingStateKey(raw);
  const labeled = formatDimensionValue("state", key);
  return labeled || raw.trim();
}

/** `Pending book → Shipped` header for a transition group. */
export function formatStateChangeLabel(fromState: string, toState: string): string {
  return `${formatBookingStateLabel(fromState)} → ${formatBookingStateLabel(toState)}`;
}

function parseTransitionFromTitle(title: string): StateChangeTransition | null {
  const match = title.match(
    /booking\s+\S+\s*[·\-–—]\s*(.+?)\s*(?:→|->)\s*(.+)$/i,
  );
  if (!match) return null;
  const fromState = match[1]?.trim();
  const toState = match[2]?.trim();
  if (!fromState || !toState) return null;
  return { fromState, toState };
}

/** Whether a child effect is a per-booking state transition. */
export function isBookingStateChangeEvent(event: FeedEvent): boolean {
  if (!STATE_CHANGE_TYPES.has(event.event_type)) return false;
  return bookingStateChangeTransition(event) != null;
}

/** `from → to` for a state-change child, from payload or title fallback. */
export function bookingStateChangeTransition(
  event: FeedEvent,
): StateChangeTransition | null {
  const fromState = readPayloadString(event.payload, "from_state");
  const toState = readPayloadString(event.payload, "to_state");
  if (fromState && toState) return { fromState, toState };
  if (event.event_type === "booking_rolled") {
    return {
      fromState: fromState ?? "unknown",
      toState: toState ?? "rolled",
    };
  }
  return parseTransitionFromTitle(event.title);
}

/** Booking source id for a state-change child. */
export function bookingStateChangeBookingId(event: FeedEvent): string | null {
  return (
    event.reference_id ??
    readPayloadString(event.payload, "source_id") ??
    null
  );
}

function stateRank(raw: string): number {
  const key = normalizeBookingStateKey(raw);
  const index = STATE_ORDER.indexOf(key);
  return index === -1 ? STATE_ORDER.length : index;
}

function transitionKey(fromState: string, toState: string): string {
  return `${normalizeBookingStateKey(fromState)}→${normalizeBookingStateKey(toState)}`;
}

/**
 * Bucket state-change children by `from → to`. Groups are ordered by booking
 * count (largest first), then by destination / origin lifecycle.
 */
export function groupStateChangeEvents(events: FeedEvent[]): StateChangeGroup[] {
  const buckets = new Map<string, StateChangeGroup>();

  for (const event of events) {
    const transition = bookingStateChangeTransition(event);
    if (!transition) continue;
    const key = transitionKey(transition.fromState, transition.toState);
    const existing = buckets.get(key);
    if (existing) {
      existing.events.push(event);
      continue;
    }
    buckets.set(key, {
      key,
      fromState: transition.fromState,
      toState: transition.toState,
      events: [event],
    });
  }

  return [...buckets.values()].sort((a, b) => {
    if (b.events.length !== a.events.length) {
      return b.events.length - a.events.length;
    }
    const toDelta = stateRank(a.toState) - stateRank(b.toState);
    if (toDelta !== 0) return toDelta;
    return stateRank(a.fromState) - stateRank(b.fromState);
  });
}

/** Split children into state-change groups and leftover effect rows. */
export function partitionFeedEffects(children: FeedEvent[]): {
  stateGroups: StateChangeGroup[];
  other: FeedEvent[];
} {
  const stateChanges: FeedEvent[] = [];
  const other: FeedEvent[] = [];
  for (const child of children) {
    if (isBookingStateChangeEvent(child)) stateChanges.push(child);
    else other.push(child);
  }
  return {
    stateGroups: groupStateChangeEvents(stateChanges),
    other,
  };
}

/** Type guard for a structured booking state-change payload. */
export function isBookingStateChangedPayload(
  payload: unknown,
): payload is BookingStateChangedPayload {
  if (!payload || typeof payload !== "object") return false;
  const record = payload as Record<string, unknown>;
  return (
    record.kind === "booking" &&
    typeof record.source_id === "string" &&
    typeof record.from_state === "string" &&
    typeof record.to_state === "string"
  );
}
